<script setup>
/**
 * Canned responses.
 *
 * Archetype E, same as Tags: shell header for the title and the primary action,
 * body for the description, search, count and table. The short-code column keeps
 * its sort toggle - it is the only way to reorder this list.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const { getPlainText } = useMessageFormatter();

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const sortOrder = ref('asc');
const searchQuery = ref('');

const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'short_code', weight: 4 },
    'content',
  ]);
});
const uiFlags = computed(() => getters.getUIFlags.value);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const toggleSort = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
};

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
  ];
});
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('CANNED_MGMT.HEADER')"
    :description="$t('CANNED_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <Button
        :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
        size="sm"
        @click="openAddPopup"
      />
    </template>

    <template #tabs>
      <div class="flex items-center justify-between gap-3">
        <SettingsSearchInput
          v-model="searchQuery"
          :placeholder="$t('CANNED_MGMT.SEARCH_PLACEHOLDER')"
        />
        <span v-if="records.length" class="text-sm text-n-slate-11">
          {{ $t('CANNED_MGMT.COUNT', { n: records.length }) }}
        </span>
      </div>
    </template>

    <BaseTable
      :headers="tableHeaders"
      :items="filteredRecords"
      :loading="uiFlags.fetchingList"
      :no-data-message="
        searchQuery ? $t('CANNED_MGMT.NO_RESULTS') : $t('CANNED_MGMT.LIST.404')
      "
    >
      <template #header-0>
        <button
          class="flex items-center gap-2 p-0 cursor-pointer"
          @click="toggleSort"
        >
          <span class="mb-0">
            {{ tableHeaders[0] }}
          </span>
          <Icon
            class="size-5 text-n-slate-11 flex-shrink-0"
            :icon="
              sortOrder === 'desc'
                ? 'i-woot-sort-descending'
                : 'i-woot-sort-ascending'
            "
          />
        </button>
      </template>
      <template #header-1>
        {{ tableHeaders[1] }}
      </template>

      <template #row="{ items }">
        <BaseTableRow
          v-for="cannedItem in items"
          :key="cannedItem.short_code"
          :item="cannedItem"
        >
          <template #default>
            <BaseTableCell class="max-w-0">
              <div class="flex flex-col gap-2 min-w-0">
                <span class="text-heading-3 text-n-slate-12 truncate block">
                  {{ cannedItem.short_code }}
                </span>
                <p class="text-body-main text-n-slate-11 line-clamp-5">
                  {{ getPlainText(cannedItem.content) }}
                </p>
              </div>
            </BaseTableCell>

            <BaseTableCell align="end" class="w-24">
              <div class="flex gap-3 justify-end flex-shrink-0">
                <Button
                  v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  @click="openEditPopup(cannedItem)"
                />
                <Button
                  v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-woot-bin"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :is-loading="loading[cannedItem.id]"
                  @click="openDeletePopup(cannedItem)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <ConfirmDialog
      v-model:show="showDeleteConfirmationPopup"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :description="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :description-value="deleteMessage"
      :confirm-button-label="deleteConfirmText"
      :cancel-button-label="deleteRejectText"
      @close="closeDeletePopup"
      @confirm="confirmDeletion"
    />
  </SettingsPageShell>
</template>
